/**
 * 综合练习（模拟单元卷）生成器
 * ------------------------------------------------------------
 * 按「配比」从单词/句子/语法里混合抽题，支持：
 *   - 题量：10 / 20 / 30 或自定义
 *   - 来源：本单元 / 多单元（跨单元复习） / 仅教材原题 / 含导入题
 *   - 策略：错题优先 / 薄弱优先 / 随机
 *
 * 配比可在 DEFAULT_MIX 里调整，也可由调用方传入 mix。
 */
import { registerGenerator } from './registry.js';
import wordGen from './wordLevels.js';
import sentenceGen from './sentenceLevels.js';
import grammarGen from './grammarGenerator.js';
import { shuffle, sample } from '../../utils/index.js';

/** 默认配比：单词 40% / 句子 35% / 语法 25% */
export const DEFAULT_MIX = { word: 0.4, sentence: 0.35, grammar: 0.25 };

export const COMPREHENSIVE_PRESETS = [
  { id: 'quick', name: '小测验', count: 10, desc: '10 题，快速检测本单元掌握情况' },
  { id: 'standard', name: '单元卷', count: 20, desc: '20 题，覆盖单词/句子/语法' },
  { id: 'full', name: '综合大卷', count: 30, desc: '30 题，接近单元测试卷强度' },
];

function splitCount(count, mix) {
  const w = Math.round(count * mix.word);
  const s = Math.round(count * mix.sentence);
  const g = Math.max(0, count - w - s);
  return { w, s, g };
}

/**
 * build({ content, count, mix, priority, mode })
 * @param {object} content 单元内容（跨单元时由调用方合并后传入）
 * @param {number} count
 * @param {object} mix     { word, sentence, grammar }
 * @param {Set}    priority 优先出现的 refId 集合（错题/薄弱点）
 * @param {string} mode    'mixed' | 'textbook-only' | 'generated-only'
 */
function build({ content = {}, count = 20, mix = DEFAULT_MIX, priority = null, mode = 'mixed' } = {}) {
  const { w, s, g } = splitCount(count, mix);
  const out = [];

  if (w > 0) {
    // 综合卷里单词用 L1~L3 的识别题为主，避免过长
    out.push(...wordGen.build({ content, level: 7, count: w, priority }));
  }
  if (s > 0) {
    const sentenceLevels = [1, 3, 5, 6];
    for (let i = 0; i < s; i++) {
      const lv = sentenceLevels[i % sentenceLevels.length];
      out.push(...sentenceGen.build({ content, level: lv, count: 1, priority }));
    }
  }
  if (g > 0) {
    out.push(...grammarGen.build({ content, count: g, category: '' }));
  }

  let list = out;
  if (mode === 'textbook-only') {
    const only = list.filter(q => q.origin === 'textbook');
    list = only.length ? only : list;
  } else if (mode === 'generated-only') {
    const only = list.filter(q => q.origin !== 'textbook');
    list = only.length ? only : list;
  }

  // 去重（同一 refId + 同题型只留一题）
  const seen = new Set();
  list = list.filter(q => {
    const k = `${q.type}|${q.refId || q.prompt}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return shuffle(list).slice(0, count);
}

export const comprehensiveGenerator = registerGenerator({
  id: 'comprehensive',
  module: 'custom',
  title: '综合练习',
  levels: COMPREHENSIVE_PRESETS,
  build,
});

export default comprehensiveGenerator;
